import React from 'react'
import PropTypes from 'prop-types'
import { identity, head, last, isNil, isEmpty } from 'ramda'

import { useSelector } from 'hooks/redux'
import { getIsPtoEditBlocked } from 'selectors/powerTakeOff'

import { yup } from 'schemas/validation'
import { BOOLEAN, RANGE, STATE, UNDEFINED } from 'utils/constants'
import { UPDATE_PLUS_PTO } from 'constants/authorization'
import strings from 'utils/strings'
import { closestValues } from 'utils/math'

import { RadioButtonGroup, Paragraph } from 'components/patterns'
import { NumberInput } from 'components/patterns/Inputs'
import { fragmentBooleanText } from 'i18n/displayConstants/powerTakeOff'

import HasRights from 'components/HasRights'
import ReadOnlyView from '../ReadOnlyView'
import {
  StyledParagraph,
  StyledUl,
  StyledSelectMenu,
  Setting,
  ModuleAndDescContainer,
} from './DynamicInput.style'

const DynamicInput = ({
  type,
  makeLabel,
  units,
  definedValues,
  translations,
  onChange,
  value,
  description,
  moduleNo,
  name,
  rules,
  disabled,
  refNo,
  children,
  hideClosestValues,
  readOnly,
}) => {
  const isPtoEditBlocked = useSelector(getIsPtoEditBlocked)
  const formatSelectButtons = () =>
    definedValues.map((definedValue) => ({
      key: definedValue,
      value: definedValue,
      title: definedValue,
    }))

  const formatRadioButtons = () =>
    translations.map((translation) => ({
      key: translation.key,
      value: translation.value,
      text: fragmentBooleanText(translation.value),
      disabled: isEmpty(definedValues) && value !== translation.value,
    }))

  const renderNumberInput = ({ readOnly }) => {
    const numberLabel = makeLabel(
      units || strings.input_value // TODO: translate units ?
    )

    const minValue = head(definedValues)
    const maxValue = last(definedValues)

    const [lowerBound, upperBound] = disabled
      ? []
      : closestValues(value, definedValues)

    const hint = minValue && maxValue ? `${minValue}-${maxValue}` : null

    if (readOnly) return <ReadOnlyView label={numberLabel} value={value} />

    return (
      <>
        <NumberInput
          hint={hint}
          label={numberLabel}
          name={name}
          value={value}
          onChange={onChange}
          rules={rules}
          disabled={disabled}
        />

        {!hideClosestValues &&
          !disabled &&
          !isNil(value) &&
          !definedValues.includes(value) && (
            <>
              <StyledParagraph>{strings.closest_values} </StyledParagraph>
              <StyledUl>
                <li>{lowerBound}</li>
                {upperBound && <li>{upperBound}</li>}
              </StyledUl>
            </>
          )}
      </>
    )
  }

  const renderRadioButtons = ({ readOnly }) => {
    if (readOnly)
      return (
        <ReadOnlyView
          label={makeLabel()}
          value={value}
          options={formatRadioButtons()}
        />
      )

    return (
      <RadioButtonGroup
        vertical
        label={makeLabel()}
        onChange={(e) => onChange(e.target.value)}
        currentValue={value}
        buttons={formatRadioButtons()}
        disabled={disabled}
      />
    )
  }

  const renderSelectMenu = ({ readOnly }) => {
    if (readOnly)
      return (
        <ReadOnlyView
          label={makeLabel(strings.value)}
          value={value}
          options={formatSelectButtons()}
        />
      )

    return (
      <StyledSelectMenu
        vertical
        label={makeLabel(strings.value)}
        options={formatSelectButtons()}
        onChange={onChange}
        value={value}
        name={name}
        rules={rules}
        disabled={disabled}
      />
    )
  }

  const renderUnknown = ({ readOnly }) => {
    if (readOnly) return <ReadOnlyView label={makeLabel()} value={value} />

    return (
      <NumberInput
        disabled
        label={makeLabel()}
        name={name}
        value={null}
        onChange={onChange}
        rules={rules}
      />
    )
  }

  const renderStrategy = {
    [BOOLEAN]: renderRadioButtons,
    [STATE]: renderSelectMenu,
    [RANGE]: renderNumberInput,
    [UNDEFINED]: renderUnknown,
  }

  const renderModuleAndDescription = (settingInfo, title) =>
    !isNil(settingInfo) && (
      <ModuleAndDescContainer>
        <Paragraph weight="bold">{title}: &nbsp;</Paragraph>
        <Paragraph>{settingInfo}</Paragraph>
      </ModuleAndDescContainer>
    )

  return (
    <Setting className={`className-${refNo}`}>
      <Paragraph weight="bold">
        ({strings.ref} {refNo})
      </Paragraph>
      <HasRights
        rights={UPDATE_PLUS_PTO}
        render={(hasRights) => (
          <>
            {renderStrategy[type]({
              readOnly: !hasRights || isPtoEditBlocked || readOnly,
              readOnlyMessage: null,
            })}
          </>
        )}
      />
      {children}
      {renderModuleAndDescription(moduleNo, strings.modules)}
      {renderModuleAndDescription(description, strings.setting_desc)}
    </Setting>
  )
}

DynamicInput.defaultProps = {
  makeLabel: identity,
  onChange: identity,
  rules: yup.mixed(),
  definedValues: [],
  disabled: false,
  type: UNDEFINED,
  translations: [],
  readOnly: false,
}

DynamicInput.propTypes = {
  className: PropTypes.string,
  type: PropTypes.oneOf([BOOLEAN, STATE, RANGE, UNDEFINED]),
  makeLabel: PropTypes.func,
  onChange: PropTypes.func,
  rules: PropTypes.object,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  description: PropTypes.string,
  moduleNo: PropTypes.string,
  name: PropTypes.string,
  translations: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.bool,
    })
  ),
  definedValues: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])
  ),
  disabled: PropTypes.bool,
  units: PropTypes.string,
  refNo: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  hideClosestValues: PropTypes.bool,
  readOnly: PropTypes.bool,
}

export default DynamicInput
